/**
 * Cookie Consent Banner
 *
 * GDPR/CCPA compliant cookie consent banner with:
 * - Clear Accept All / Reject All options
 * - Granular preference controls
 * - Accessible design (keyboard navigation, ARIA)
 * - Non-blocking UI (bottom banner)
 * - Preference management modal
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, Settings, Shield, BarChart2, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useConsent } from '@/contexts/ConsentContext';
import { cn } from '@/lib/utils';

// Animation variants
const bannerVariants = {
  hidden: { y: 100, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 300, damping: 30 } },
  exit: { y: 100, opacity: 0, transition: { duration: 0.2 } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
};

// Preference category card component
interface PreferenceCategoryProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  enabled: boolean;
  disabled?: boolean;
  onChange: (enabled: boolean) => void;
}

function PreferenceCategory({
  icon,
  title,
  description,
  enabled,
  disabled = false,
  onChange,
}: PreferenceCategoryProps) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50/50">
      <div className="flex-shrink-0 p-2 rounded-full bg-gray-100 text-gray-600">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-4">
          <h4 className="text-sm font-medium text-gray-900">{title}</h4>
          <Switch
            checked={enabled}
            onCheckedChange={onChange}
            disabled={disabled}
            aria-label={`Toggle ${title}`}
          />
        </div>
        <p className="mt-1 text-xs text-gray-600">{description}</p>
      </div>
    </div>
  );
}

// Main Cookie Consent component
export function CookieConsent() {
  const {
    hasConsented,
    consentState,
    acceptAll,
    rejectAll,
    setPreferences,
    showPreferences,
    setShowPreferences,
  } = useConsent();

  // Modal ref for focus trap
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<Element | null>(null);

  // Local state for preference editing
  const [localPreferences, setLocalPreferences] = useState({
    analytics: consentState.analytics,
    marketing: consentState.marketing,
  });

  // Sync local preferences when consent state changes
  useEffect(() => {
    setLocalPreferences({
      analytics: consentState.analytics,
      marketing: consentState.marketing,
    });
  }, [consentState]);

  // Focus trap for preferences modal
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowPreferences(false);
      return;
    }

    if (event.key !== 'Tab' || !modalRef.current) {
      return;
    }

    const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement?.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement?.focus();
    }
  }, [setShowPreferences]);

  // Manage focus when modal opens/closes
  useEffect(() => {
    if (showPreferences) {
      // Store current focus and focus modal
      previousActiveElementRef.current = document.activeElement;
      document.addEventListener('keydown', handleKeyDown);

      // Focus first focusable element in modal
      requestAnimationFrame(() => {
        const firstFocusable = modalRef.current?.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus();
      });
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to previous element
      if (previousActiveElementRef.current instanceof HTMLElement) {
        previousActiveElementRef.current.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPreferences, handleKeyDown]);

  // Don't show anything if user has already consented
  if (hasConsented && !showPreferences) {
    return null;
  }

  const handleSavePreferences = () => {
    // setPreferences already closes modal via ConsentContext
    setPreferences(localPreferences);
  };

  return (
    <>
      {/* Main Banner */}
      {/*
        Consent banner. It is `fixed` and overlays page content, so at compact
        widths it must stay short and its actions must be reachable: tighter
        padding plus a scroll cap keep it from swallowing the viewport, a
        safe-area inset keeps the buttons clear of the iOS home indicator, and
        every action carries a >=44px touch target. Layout classes only —
        handlers, ARIA, and the focus trap below are unchanged.
      */}
      <AnimatePresence>
        {!hasConsented && !showPreferences && (
          <motion.div
            key="consent-banner"
            variants={bannerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-4 bg-white border-t border-gray-200 shadow-lg"
            role="dialog"
            aria-modal="false"
            aria-label="Cookie consent"
          >
            <div className="max-w-7xl mx-auto max-h-[55vh] overflow-y-auto">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-3 sm:gap-4">
                {/* Icon and Text */}
                <div className="flex items-start gap-3 flex-1">
                  <div className="flex-shrink-0 p-2 rounded-full bg-amber-100 text-amber-600">
                    <Cookie className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">
                      We value your privacy
                    </h3>
                    <p className="mt-1 text-[13px] leading-snug sm:text-sm text-gray-600">
                      We use cookies to enhance your browsing experience, analyze site traffic, and personalize content.
                      You can choose to accept all cookies or customize your preferences.{' '}
                      <a
                        href="/privacy"
                        className="text-amber-600 hover:text-amber-700 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn more
                      </a>
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreferences(true)}
                    className="flex-1 min-h-[44px] md:flex-none"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Customize
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={rejectAll}
                    className="flex-1 min-h-[44px] md:flex-none"
                  >
                    Reject All
                  </Button>
                  <Button
                    size="sm"
                    onClick={acceptAll}
                    className="flex-1 min-h-[44px] md:flex-none bg-gray-900 hover:bg-gray-800 text-white"
                  >
                    Accept All
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preferences Modal */}
      <AnimatePresence>
        {showPreferences && (
          <>
            {/* Backdrop */}
            <motion.div
              key="preferences-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] bg-black/50"
              onClick={() => setShowPreferences(false)}
              aria-hidden="true"
            />

            {/* Modal */}
            <motion.div
              key="preferences-modal"
              ref={modalRef}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[10000] max-w-lg mx-auto bg-white rounded-xl shadow-2xl overflow-hidden md:inset-x-auto"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookie-preferences-title"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-100 text-amber-600">
                    <Cookie className="h-5 w-5" />
                  </div>
                  <h2 id="cookie-preferences-title" className="text-lg font-semibold text-gray-900">
                    Cookie Preferences
                  </h2>
                </div>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Close preferences"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
                <p className="text-sm text-gray-600">
                  Manage your cookie preferences below. Essential cookies are required for the
                  website to function and cannot be disabled.
                </p>

                {/* Essential Cookies */}
                <PreferenceCategory
                  icon={<Shield className="h-4 w-4" />}
                  title="Essential Cookies"
                  description="Required for the website to function. These cookies enable core functionality such as security, network management, and accessibility."
                  enabled={true}
                  disabled={true}
                  onChange={() => {}}
                />

                {/* Analytics Cookies */}
                <PreferenceCategory
                  icon={<BarChart2 className="h-4 w-4" />}
                  title="Analytics Cookies"
                  description="Help us understand how visitors interact with our website by collecting and reporting information anonymously."
                  enabled={localPreferences.analytics}
                  onChange={(enabled) => setLocalPreferences(prev => ({ ...prev, analytics: enabled }))}
                />

                {/* Marketing Cookies */}
                <PreferenceCategory
                  icon={<Target className="h-4 w-4" />}
                  title="Marketing Cookies"
                  description="Used to track visitors across websites to display relevant advertisements and measure the effectiveness of campaigns."
                  enabled={localPreferences.marketing}
                  onChange={(enabled) => setLocalPreferences(prev => ({ ...prev, marketing: enabled }))}
                />
              </div>

              {/* Footer */}
              <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-2 p-4 border-t border-gray-200 bg-gray-50">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={rejectAll}
                  className="w-full min-h-[44px] sm:w-auto"
                >
                  Reject All
                </Button>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={acceptAll}
                    className="flex-1 min-h-[44px] sm:flex-none"
                  >
                    Accept All
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSavePreferences}
                    className="flex-1 min-h-[44px] sm:flex-none bg-gray-900 hover:bg-gray-800 text-white"
                  >
                    Save Preferences
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Floating button to re-open cookie preferences
 * Shows only after user has consented (for GDPR right to withdraw)
 */
export function CookiePreferencesButton() {
  const { hasConsented, setShowPreferences } = useConsent();

  if (!hasConsented) return null;

  return (
    <button
      onClick={() => setShowPreferences(true)}
      className={cn(
        'fixed bottom-4 left-4 z-[999] p-3 rounded-full',
        'flex min-h-[44px] min-w-[44px] items-center justify-center',
        'bg-gray-900 text-white shadow-lg',
        'hover:bg-gray-800 transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'
      )}
      aria-label="Cookie preferences"
      title="Manage cookie preferences"
    >
      <Cookie className="h-5 w-5" />
    </button>
  );
}

export default CookieConsent;
